import type { UdsTokens } from '@sunwebgroup/ui-components'
import type { ComponentPropsWithoutRef } from 'react'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import * as React from 'react'
import { useRef } from 'react'

import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { HeaderDependenciesProvider } from '@/scopes/common/components/Header/dependencies'
import { LanguageSwitch } from '@/scopes/common/components/Header/LanguageSwitch'
import TopNavigationBarItem from '@/scopes/common/components/Header/TopNavigationBarItem'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useFavouriteProductIdsQuery } from '@/scopes/favourites/domain/hooks'
import { createLiveSearchApiClient } from '@/scopes/search/search/infrastructure/live-search/api/liveSearchApiClient'
import { LiveSearch } from '../../../search/search/pages/SearchPage/components/Header/LiveSearch'
import { AccountMenu } from './AccountMenu'
import { DesktopNavigation } from './DesktopNavigation'
import styles from './Header.module.css'
import { HeaderContainer } from './HeaderContainer'
import { MobileNavigation } from './MobileNavigation'
// TODO: Remove this import when the Header component is connected to Sitecore
import mockData, { mockDataExtra } from './mock-data'

// Extend the props to include hideLiveSearch
interface HeaderProps extends ComponentPropsWithoutRef<'header'> {
  hideLiveSearch?: boolean
  hideNavItems?: boolean
  backButton?: React.ReactNode
  backgroundColor?: 'transparent' | keyof UdsTokens
}

export function Header({
  hideLiveSearch = false,
  hideNavItems = false,
  backButton,
  backgroundColor,
}: HeaderProps) {
  const { data: favourites } = useFavouriteProductIdsQuery()
  const links = useConfiguration('links')
  const contextItemId = useCMSPageField('contextItemId')
  const homePageId = useCMSPageField('HomePage.id')
  const pdpId = useCMSPageField('PDP.id')
  const liveSearchContextItemId = contextItemId ?? pdpId ?? homePageId
  const showLiveSearch = !hideLiveSearch && liveSearchContextItemId
  const ref = useRef<HTMLDivElement>(null)
  const intl = useIntl()
  const headerMainMenu = useCMSPageField('Header.mainMenu')
  const mainContent = hideNavItems ? undefined : headerMainMenu?.mainItem ?? mockData
  const extraContent = hideNavItems ? undefined : headerMainMenu?.extraItems ?? mockDataExtra

  return (
    <HeaderDependenciesProvider
      dependencies={{
        liveSearchApiClient: createLiveSearchApiClient({ contextItemId: liveSearchContextItemId }),
      }}
    >

      <HeaderContainer backgroundColor={backgroundColor}>
        <DesktopNavigation
          data={mainContent}
          extraData={extraContent}
          headerWrapperElement={ref.current ?? undefined}
          backButton={backButton}
        />
        <div className={styles.iconsWrapper}>
          <LanguageSwitch />
          {showLiveSearch && <LiveSearch />}
          {!hideNavItems && (
            <>
              <TopNavigationBarItem
                as="a"
                href={links.favourites}
                aria-label={intl.formatMessage({ id: 'header_my_favourites' })}
                data-qa="favourites-link"
                data-qa-favourites-count={favourites?.length ?? 0}
              >
                <Icon glyph="Heart" className={styles.favoritesIcon} />
                <Typography>{favourites?.length ?? 0}</Typography>
              </TopNavigationBarItem>
            </>
          )}
          <AccountMenu />
          {!hideNavItems && <MobileNavigation data={mainContent} extraData={extraContent} />}
        </div>
      </HeaderContainer>
    </HeaderDependenciesProvider>
  )
}
